/**
 * ParallaxImage – slight scroll effect on hero media.
 * Uses the hero section for scroll range so it works regardless of wrapper layout.
 * Respects prefers-reduced-motion.
 */
function getOffsetTop (el) {
  const rect = el.getBoundingClientRect()
  return rect.top + (window.scrollY ?? window.pageYOffset)
}

function getIsXsViewport () {
  return window.matchMedia('(max-width: 767px)').matches
}

class ParallaxImage extends HTMLElement {
  connectedCallback () {
    /* eslint-disable-next-line no-undef */
    if (typeof Shopify !== 'undefined' && Shopify.designMode) return

    this._onScroll = this._onScroll.bind(this)
    this._updateEl = this._updateEl.bind(this)

    this._isTicking = false
    this._lastKnownY = window.scrollY ?? window.pageYOffset

    this._updateEl()
    window.addEventListener('scroll', this._onScroll, { passive: true })

    window.setTimeout(() => {
      this.setAttribute('active', '')
    }, 500)
  }

  disconnectedCallback () {
    window.removeEventListener('scroll', this._onScroll)
  }

  _onScroll () {
    this._lastKnownY = window.scrollY ?? window.pageYOffset
    if (!this._isTicking) {
      this._isTicking = true
      requestAnimationFrame(() => {
        this._isTicking = false
        this._updateEl()
      })
    }
  }

  _updateEl () {
    this._moveParallaxItem()
  }

  _moveParallaxItem () {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reducedMotion) return

    const section = this.closest('.sec-Hero')
    if (!section) return

    const elementHeight = section.offsetHeight
    if (!elementHeight) return

    const amountToMoveY = getIsXsViewport() ? 40 : 60
    const scaledItemHeight = elementHeight + amountToMoveY
    const heightDifference = scaledItemHeight - elementHeight

    const elementStartPoint = getOffsetTop(section) - window.innerHeight
    const elementEndPoint = getOffsetTop(section) + elementHeight

    if (this._lastKnownY <= elementStartPoint) {
      this.style.transform = `translate3d(0px, ${amountToMoveY / -2}px, 0px)`
      return
    }

    if (this._lastKnownY > elementEndPoint) {
      this.style.transform = `translate3d(0px, ${amountToMoveY / 2}px, 0px)`
      return
    }

    const totalScrollAmount = elementEndPoint - elementStartPoint
    const scrollYAsAPercentageOfJourney =
      (this._lastKnownY - elementStartPoint) / totalScrollAmount
    const transformAmountY =
      heightDifference * scrollYAsAPercentageOfJourney - heightDifference / 2

    this.style.transform = `translate3d(0px, ${transformAmountY}px, 0px)`
  }
}

if (!customElements.get('parallax-image')) {
  customElements.define('parallax-image', ParallaxImage)
}

export default ParallaxImage
